import { isAxiosError } from 'axios';
import {
  Download,
  File,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Image,
  MoreVertical,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  type LoanDocument,
  getDocumentDownloadUrl,
  useCreateDocument,
  useDeleteDocument,
  useDocuments,
  useUploadDocument,
} from '@/hooks/use-documents';
import { handleMutationError } from '@/lib/error-handling';
import { toast } from '@/stores/toast-store';

const MAX_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_UPLOAD_SIZE_LABEL = '20 MB';
const ALLOWED_UPLOAD_EXTENSIONS = [
  'PDF',
  'JPG',
  'JPEG',
  'PNG',
  'WEBP',
  'DOC',
  'DOCX',
  'XLSX',
  'CSV',
  'TXT',
];
const ALLOWED_UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xlsx,.csv,.txt';

const CATEGORIES = [
  { key: 'APPLICATION', label: 'Application Forms', icon: FileText },
  { key: 'IDENTITY', label: 'Identity Documents', icon: File },
  { key: 'FINANCIAL', label: 'Financial Statements', icon: FileSpreadsheet },
  { key: 'COLLATERAL', label: 'Collateral Documents', icon: FolderOpen },
  { key: 'AGREEMENT', label: 'Loan Agreements', icon: FileText },
  { key: 'INSURANCE', label: 'Insurance Documents', icon: File },
  { key: 'CORRESPONDENCE', label: 'Correspondence', icon: FileText },
  { key: 'OTHER', label: 'Other', icon: File },
];

function getFileIcon(mimeType: string) {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return FileSpreadsheet;
  if (mimeType?.includes('pdf')) return FileText;
  return File;
}

function formatSize(bytes: number): string {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface Props {
  loanId: string;
}

export function DocumentsTab({ loanId }: Props) {
  const { data: documents = [], isLoading } = useDocuments(loanId);
  const uploadDoc = useUploadDocument();
  const createDoc = useCreateDocument();
  const deleteDoc = useDeleteDocument();

  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('OTHER');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Manual entry fields (for metadata-only records)
  const [manualMode, setManualMode] = useState(false);
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('PDF');

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setUploadError(`File too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`);
      setSelectedFile(null);
      return;
    }
    setUploadError('');
    setSelectedFile(file);
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setUploadError(`File too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`);
      setSelectedFile(null);
      return;
    }
    setUploadError('');
    setSelectedFile(file);
  }, []);

  useEffect(() => {
    const openUpload = () => setShowUpload(true);
    window.addEventListener('loan-documents:open-upload', openUpload);
    return () => window.removeEventListener('loan-documents:open-upload', openUpload);
  }, []);

  function handleUpload() {
    setUploadError('');
    if (manualMode) {
      if (!docName.trim()) {
        toast.error('Please enter a document name');
        return;
      }
      createDoc.mutate(
        { loanId, name: docName, type: docType, category: selectedCategory },
        {
          onSuccess: () => {
            toast.success('Document record added');
            resetForm();
          },
          onError: (err: any) => handleMutationError(err, 'Failed to add document'),
        },
      );
    } else {
      if (!selectedFile) {
        toast.error('Please select a file to upload');
        return;
      }
      if (selectedFile.size > MAX_UPLOAD_SIZE_BYTES) {
        setUploadError(`File too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`);
        return;
      }
      uploadDoc.mutate(
        { loanId, file: selectedFile, category: selectedCategory },
        {
          onSuccess: doc => {
            toast.success(`Uploaded "${doc.name}"`);
            resetForm();
          },
          onError: (err: any) => {
            if (isAxiosError(err) && err.response?.status === 413) {
              setUploadError(`File too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`);
              return;
            }
            handleMutationError(err, 'Upload failed');
          },
        },
      );
    }
  }

  function resetForm() {
    setShowUpload(false);
    setSelectedFile(null);
    setUploadError('');
    setDocName('');
    setDocType('PDF');
    setSelectedCategory('OTHER');
    setManualMode(false);
  }

  function handleDelete(doc: LoanDocument) {
    if (window.confirm(`Delete "${doc.name}"?`)) {
      deleteDoc.mutate(
        { docId: doc.id, loanId },
        {
          onSuccess: () => toast.success('Document deleted'),
          onError: (err: any) => handleMutationError(err, 'Failed to delete document'),
        },
      );
    }
  }

  function handleDownload(doc: LoanDocument) {
    const url = getDocumentDownloadUrl(doc.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    a.click();
  }

  const filteredDocuments = documents
    .filter(doc => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      const categoryLabel = CATEGORIES.find(cat => cat.key === doc.category)?.label ?? doc.category;
      return [doc.name, doc.type, categoryLabel].some(value => value.toLowerCase().includes(query));
    })
    .sort(
      (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
    );

  const isUploading = uploadDoc.isPending || createDoc.isPending;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showUpload && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {manualMode ? 'Add Document Record' : 'Upload Document'}
            </h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setManualMode(!manualMode)}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                {manualMode ? 'Switch to file upload' : 'Add without file'}
              </button>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Category selection */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map(cat => (
                <Button
                  variant="secondary"
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`flex items-center gap-2 p-2 rounded-lg border text-xs font-medium transition-colors ${
                    selectedCategory === cat.key
                      ? 'border-blue-500 bg-indigo-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <cat.icon className="w-3.5 h-3.5" />
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>

          {manualMode ? (
            /* Manual entry mode */
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Document Name
                </label>
                <input
                  value={docName}
                  onChange={e => setDocName(e.target.value)}
                  placeholder="e.g. Tax Return 2024"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={docType}
                  onChange={e => setDocType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="PDF">PDF</option>
                  <option value="IMAGE">Image</option>
                  <option value="SPREADSHEET">Spreadsheet</option>
                  <option value="DOCUMENT">Document</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
          ) : (
            /* File upload mode */
            <div className="mb-3">
              <div
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  isDragging
                    ? 'border-blue-500 bg-indigo-50'
                    : selectedFile
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-green-500" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{formatSize(selectedFile.size)}</p>
                    </div>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      className="ml-2 p-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Allowed: {ALLOWED_UPLOAD_EXTENSIONS.join(', ')}. Max {MAX_UPLOAD_SIZE_LABEL}.
                    </p>
                  </>
                )}
                <input
                  type="file"
                  accept={ALLOWED_UPLOAD_ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                  id="doc-file-input"
                />
                {!selectedFile && (
                  <label
                    htmlFor="doc-file-input"
                    className="inline-block mt-3 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    Browse Files
                  </label>
                )}
              </div>
              {uploadError && (
                <p className="mt-2 text-xs font-medium text-red-600">{uploadError}</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={resetForm}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-1.5 text-white rounded-lg text-sm font-medium hover: disabled:opacity-50"
            >
              {isUploading && <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />}
              {isUploading ? 'Uploading...' : manualMode ? 'Add Record' : 'Upload'}
            </Button>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <FolderOpen className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">No documents yet</p>
          <p className="mt-1 text-sm text-gray-400">
            Upload loan documents to keep everything organized.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-950">Document Inventory</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filteredDocuments.length} item{filteredDocuments.length !== 1 ? 's' : ''} attached
                to this loan file
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative w-full min-w-0 lg:w-[280px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search files..."
                  className="w-full rounded-md border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm text-gray-700 outline-none transition-colors focus:border-gray-400"
                />
              </div>
              <button
                type="button"
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="More actions"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="p-12 text-center">
              <FolderOpen className="mx-auto mb-3 h-10 w-10 text-gray-300" />
              <p className="text-sm font-medium text-gray-700">No matching documents</p>
              <p className="mt-1 text-sm text-gray-400">
                Try a different search term or upload a new file.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                    <th className="px-6 py-4">Document Name</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Last Action</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredDocuments.map(doc => {
                    const DocIcon = getFileIcon(doc.mimeType);
                    const categoryLabel =
                      CATEGORIES.find(cat => cat.key === doc.category)?.label ?? doc.category;
                    return (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-6 py-5">
                          <div className="flex items-start gap-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-gray-50">
                              <DocIcon className="h-4 w-4 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {doc.name}
                              </p>
                              <p className="mt-1 text-xs text-gray-400">
                                {formatSize(doc.fileSize)} · ID: {doc.id.slice(0, 8)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-sm text-gray-700">
                          {doc.type || 'Document'}
                        </td>
                        <td className="px-6 py-5 text-sm text-gray-600">{categoryLabel}</td>
                        <td className="px-6 py-5 text-sm text-gray-600">
                          <div>{fmtDate(doc.updatedAt)}</div>
                          <div className="mt-1 text-xs text-gray-400">
                            {doc.uploadedBy
                              ? `${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName}`
                              : 'Unknown uploader'}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-end gap-1">
                            {doc.fileUrl && (
                              <button
                                onClick={() => handleDownload(doc)}
                                className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(doc)}
                              className="rounded-md p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
